import { useLocation, useParams } from "react-router";
import { useMemo } from "react";

export const useUserLocation = () => {
	const location = useLocation();
	const params = useParams();

	// Main Landing Page
	const isMainLandingPage = location.pathname === "/";

	// My Orders
	const isOrderDetailsPage = location.pathname.includes(`/details/${params.orderId}`);

	// New Order
	const isNewOrderPage = location.pathname.includes("/new-order");
	const isNewOrderLandingPage = location.pathname === "/new-order";
	const isNewOrderBuildOrderPage = location.pathname.includes("/new-order/build-order");
	const isNewOrderViewPriceEstimatesPage = location.pathname.includes("/new-order/view-price-estimates");
	const isNewOrderShippingDetailsPage = location.pathname.includes("/new-order/shipping-details");
	const isNewOrderReviewPage = location.pathname.includes("/new-order/review-order");
	const isNewOrderConfirmationPage = location.pathname.includes("new-order/confirmation");

	// Add-Ons
	const isAddOnPage = location.pathname.includes("/add-ons/");
	const isAddOnLandingPage = location.pathname === `/add-ons/${params?.orderId}`;
	const isAddOnBuildOrderPage = location.pathname.includes(`/add-ons/build-order/${params?.orderId}`);
	const isAddOnViewPriceEstimatesPage = location.pathname.includes(
		`/add-ons/view-price-estimates/${params?.orderId}`
	);
	const isAddOnShippingDetailsPage = location.pathname.includes(`/add-ons/shipping-details/${params?.orderId}`);
	const isAddOnReviewPage = location.pathname.includes(`/add-ons/review-order/${params?.orderId}`);
	const isAddOnConfirmationPage = location.pathname.includes("add-ons/confirmation");

	// Custom Color
	const isCustomColorPage = location.pathname.includes("/custom-color/");
	const isCustomColorBuildOrderPage = location.pathname.includes("/custom-color/build-order");
	const isCustomColorViewPriceEstimatesPage = location.pathname.includes("/custom-color/view-price-estimates");
	const isCustomColorShippingDetailsPage = location.pathname.includes("/custom-color/shipping-details");
	const isCustomColorReviewPage = location.pathname.includes("/custom-color/review-order");
	const isCustomColorConfirmationPage = location.pathname.includes("custom-color/confirmation");

	// New Quote
	const isNewQuotePage = location.pathname.includes("/new-quote/");
	const isNewQuoteBuildOrderPage = location.pathname.includes("/new-quote/build-order");
	const isNewQuoteViewPriceEstimatesPage = location.pathname.includes("/new-quote/view-price-estimates");
	const isNewQuoteReviewPage = location.pathname.includes("/new-quote/review-order");
	const isNewQuoteConfirmationPage = location.pathname.includes("new-quote/confirmation");

	// Drafts
	const isNewOrderBuildOrderDraftPage = location.pathname === `/new-order/build-order/${params.draftId}`;
	const isAddOnBuildOrderDraftPage = location.pathname === `/add-ons/build-order/${params.orderId}/${params.draftId}`;
	const isBuildOrderDraftPage = isNewOrderBuildOrderDraftPage || isAddOnBuildOrderDraftPage;

	// Replacements
	const isReplacementsPage = location.pathname.includes("/replacements");
	const isReplacementsSelectPartsPage = location.pathname === `/replacements/${params?.orderId}`;
	const isReplacementsReasonPage = location.pathname.includes(`/replacements/reason/${params?.orderId}`);
	const isReplacementsShippingDetailsPage = location.pathname.includes(
		`/replacements/shipping-details/${params?.orderId}`
	);
	const isReplacementsReviewPage = location.pathname.includes(`/replacements/review/${params?.orderId}`);

	// Trucks
	const isTrucksPage = location.pathname.includes("/trucks");
	const isTrucksConfirmationPage = location.pathname.includes("/trucks/confirmation");

	// Custom Quotes
	const isCustomQuotesLandingPage = location.pathname === "/custom-quote";
	const isCustomQuotesAddQuotePage = location.pathname.includes("/add-quote");
	const isCustomQuotesCreateQuotePage = location.pathname === "/custom-quote/create-quote";

	// All New Orders Flow
	const isConfirmationPage =
		isNewOrderConfirmationPage ||
		isAddOnConfirmationPage ||
		isCustomColorConfirmationPage ||
		isTrucksConfirmationPage;
	const isBuildOrderPage =
		isNewOrderBuildOrderPage || isAddOnBuildOrderPage || isCustomColorBuildOrderPage || isNewQuoteBuildOrderPage;
	const isViewPriceEstimatesPage =
		isNewOrderViewPriceEstimatesPage ||
		isAddOnViewPriceEstimatesPage ||
		isCustomColorViewPriceEstimatesPage ||
		isNewQuoteViewPriceEstimatesPage;
	const isShippingDetailsPage =
		isNewOrderShippingDetailsPage || isAddOnShippingDetailsPage || isCustomColorShippingDetailsPage;
	const isReviewOrderPage =
		isNewOrderReviewPage || isAddOnReviewPage || isCustomColorReviewPage || isNewQuoteReviewPage;
	const isProtectedNewOrderFlowPage =
		isBuildOrderPage || isViewPriceEstimatesPage || isShippingDetailsPage || isReviewOrderPage;

	// Preferences
	const isUserSettingsPage = location.pathname === "/user-settings";

	// Error Pages
	const isUnavailablePage = location.pathname.includes("/unavailable");
	const isUnauthorizedPage = location.pathname.includes("/unauthorized");
	const isUnapprovedPage = location.pathname.includes("/unapproved");
	const isErrorRoute = useMemo(
		() => isUnavailablePage || isUnauthorizedPage || isUnapprovedPage,
		[isUnauthorizedPage, isUnapprovedPage, isUnavailablePage]
	);

	return {
		isMainLandingPage,
		isAddOnPage,
		isNewOrderPage,
		isNewOrderBuildOrderPage,
		isNewOrderLandingPage,
		isAddOnLandingPage,
		isTrucksPage,
		isCustomColorPage,
		isCustomQuotesLandingPage,
		isConfirmationPage,
		isAddOnReviewPage,
		isBuildOrderDraftPage,
		isNewOrderShippingDetailsPage,
		isNewOrderReviewPage,
		isNewOrderConfirmationPage,
		isReplacementsReviewPage,
		isReplacementsReasonPage,
		isOrderDetailsPage,
		isReplacementsPage,
		isReplacementsShippingDetailsPage,
		isErrorRoute,
		isReplacementsSelectPartsPage,
		isAddOnConfirmationPage,
		isNewOrderViewPriceEstimatesPage,
		isAddOnBuildOrderPage,
		isAddOnViewPriceEstimatesPage,
		isAddOnShippingDetailsPage,
		isCustomQuotesAddQuotePage,
		isCustomQuotesCreateQuotePage,
		isCustomColorBuildOrderPage,
		isCustomColorViewPriceEstimatesPage,
		isCustomColorShippingDetailsPage,
		isCustomColorReviewPage,
		isUserSettingsPage,
		isNewQuotePage,
		isNewQuoteViewPriceEstimatesPage,
		isNewQuoteConfirmationPage,
		isNewQuoteReviewPage,
		isBuildOrderPage,
		isViewPriceEstimatesPage,
		isShippingDetailsPage,
		isReviewOrderPage,
		isProtectedNewOrderFlowPage
	};
};
